import { Component } from '@theme/component';
import { parseSection } from '@theme/morph';
import { ThemeEvents, OverlayOpenedEvent, SearchResultsUpdatedEvent, eventBus } from '@theme/events';

/**
 * @typedef {Object} SearchOverlayRefs
 * @property {HTMLButtonElement} trigger - Search icon trigger button
 * @property {HTMLElement} panel - Full-width overlay panel
 * @property {HTMLInputElement} input - Search text input
 * @property {HTMLButtonElement} close - Close button
 * @property {HTMLElement} suggested - Suggested products container
 * @property {HTMLElement} results - Search results container
 * @property {HTMLElement} [status] - ARIA live region for screen readers
 */

/**
 * Full-width search overlay component.
 *
 * Opens a fixed panel below the header with a search bar, merchant-curated
 * suggested products, and dynamically fetched search results.
 *
 * Supports two modes:
 * - Capped: fetches from /search/suggest with a fixed limit (default)
 * - Infinite: fetches from /search with pagination and infinite scroll
 *
 * @extends {Component<SearchOverlayRefs>}
 */
class SearchOverlay extends Component {
	/** @type {AbortController|null} */
	#abortController = null;

	/** @type {AbortController|null} */
	#fetchController = null;

	/** @type {number} */
	#debounceTimer = 0;

	/** @type {number} */
	#debounceMs = 250;

	/** @type {boolean} */
	#isInfinite = false;

	/** @type {string|null} Next page URL for infinite scroll */
	#nextUrl = null;

	/** @type {IntersectionObserver|null} */
	#scrollObserver = null;

	/** @type {boolean} */
	#isLoadingMore = false;

	/** @type {IntersectionObserver|null} Preloads images 1 panel height ahead */
	#preloadObserver = null;

	connectedCallback() {
		super.connectedCallback();

		const debounceAttr = this.dataset.debounceMs;
		if (debounceAttr) {
			const parsed = parseInt(debounceAttr, 10);
			if (!isNaN(parsed) && parsed >= 0) this.#debounceMs = parsed;
		}

		this.#isInfinite = this.dataset.infinite === 'true';

		this.#abortController = new AbortController();
		const { signal } = this.#abortController;

		document.addEventListener(
			'click',
			(event) => {
				if (
					this.hasAttribute('data-active') &&
					!this.contains(/** @type {Node} */ (event.target))
				) {
					this.close();
				}
			},
			{ signal },
		);

		// Mutual exclusion: close when another surface opens
		document.addEventListener(ThemeEvents.overlayOpened, (e) => {
			if (e.detail?.source !== this && this.hasAttribute('data-active')) {
				this.close();
			}
		}, { signal });
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController?.abort();
		this.#abortController = null;
		this.#fetchController?.abort();
		this.#fetchController = null;
		this.#teardownInfiniteScroll();
		clearTimeout(this.#debounceTimer);
	}

	// -- Public methods --

	open() {
		// Stamp deferred panel template on first open (LCP optimization)
		const tpl = this.querySelector('template[data-search-panel]');
		if (tpl) {
			this.appendChild(tpl.content.cloneNode(true));
			tpl.remove();
			// Force a synchronous refs rescan — the MutationObserver callback
			// is async (microtask) so `this.refs.panel` would still be
			// undefined on this tick otherwise, making the early-return below
			// fire and blocking the first open.
			this.updatedCallback();
		}

		if (!this.refs.panel) return;

		const header = this.closest('header, .shopify-section');
		if (header) {
			this.refs.panel.style.top =
				header.getBoundingClientRect().bottom + 'px';
		}

		this.refs.panel.hidden = false;
		this.setAttribute('data-active', '');
		this.refs.trigger?.setAttribute('aria-expanded', 'true');

		requestAnimationFrame(() => this.refs.input?.focus());

		// Notify other surfaces to close (mutual exclusion)
		eventBus.emit(new OverlayOpenedEvent({ source: this }));
	}

	close() {
		if (!this.refs.panel) return;

		this.refs.panel.hidden = true;
		this.removeAttribute('data-active');
		this.refs.trigger?.setAttribute('aria-expanded', 'false');
		this.#fetchController?.abort();
		this.#teardownInfiniteScroll();
		this.#preloadObserver?.disconnect();
		this.#preloadObserver = null;
		clearTimeout(this.#debounceTimer);

		if (this.refs.input) this.refs.input.value = '';
		this.#showSuggested();
	}

	// -- Declarative event handlers --

	handleTriggerClick() {
		if (this.hasAttribute('data-active')) {
			this.close();
		} else {
			this.open();
		}
	}

	handleSubmit(event) {
		event.preventDefault();
	}

	handleClose() {
		this.close();
		this.refs.trigger?.focus();
	}

	handleInput() {
		const query = this.refs.input?.value.trim() ?? '';

		clearTimeout(this.#debounceTimer);
		this.#fetchController?.abort();
		this.#teardownInfiniteScroll();

		if (query.length === 0) {
			this.#showSuggested();
			if (this.refs.results) this.refs.results.innerHTML = '';
			if (this.refs.status) this.refs.status.textContent = '';
			return;
		}

		this.#debounceTimer = setTimeout(
			() => this.#fetchResults(query),
			this.#debounceMs,
		);
	}

	/** @param {KeyboardEvent} event */
	handleKeydown(event) {
		if (event.key === 'Escape') {
			event.preventDefault();
			this.close();
			this.refs.trigger?.focus();
		}
	}

	// -- Private methods --

	/**
	 * Preload images 1 panel height ahead of the visible area.
	 * Switches lazy images to eager when their card enters the buffer zone,
	 * so images are ready before the user scrolls to them.
	 */
	#setupImagePreloader() {
		this.#preloadObserver?.disconnect();
		this.#preloadObserver = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						for (const img of entry.target.querySelectorAll('img[loading="lazy"]')) {
							img.loading = 'eager';
						}
						this.#preloadObserver?.unobserve(entry.target);
					}
				}
			},
			{ root: this.refs.panel, rootMargin: '0px 0px 100% 0px' },
		);

		for (const card of this.refs.results.querySelectorAll('product-card')) {
			this.#preloadObserver.observe(card);
		}
	}

	/**
	 * Apply block-level card settings to injected results.
	 * The section renders with global defaults; this overrides per block config.
	 */
	#applyCardSettings() {
		if (!this.refs.results) return;
		const enableCarousel = this.dataset.enableCarousel !== 'false';
		const loop = this.dataset.carouselLoop !== 'false';
		const hoverPreview = this.dataset.showSecondOnHover === 'true';

		for (const card of this.refs.results.querySelectorAll('product-card')) {
			// Carousel on/off
			for (const carousel of card.querySelectorAll('carousel-slider')) {
				if (!enableCarousel) {
					// Replace carousel with just the first image
					const firstSlide = carousel.querySelector('.carousel__slide');
					if (firstSlide) {
						carousel.replaceWith(firstSlide);
					}
				} else {
					carousel.dataset.loop = String(loop);
				}
			}

			// Hover preview (only relevant when carousel is disabled)
			const media = card.querySelector('.product-card__media');
			if (media) {
				if (!enableCarousel && hoverPreview) {
					media.dataset.hoverPreview = 'true';
				} else if (!enableCarousel) {
					media.removeAttribute('data-hover-preview');
				}
			}
		}
	}

	#showSuggested() {
		if (this.refs.suggested) this.refs.suggested.hidden = false;
		if (this.refs.results) this.refs.results.hidden = true;
	}

	#showResults() {
		if (this.refs.suggested) this.refs.suggested.hidden = true;
		if (this.refs.results) this.refs.results.hidden = false;
	}

	/** @param {string} query */
	async #fetchResults(query) {
		this.#fetchController?.abort();
		this.#fetchController = new AbortController();

		const sectionId = this.dataset.sectionId || 'search-overlay-results';

		let url;
		if (this.#isInfinite) {
			const params = new URLSearchParams({
				q: query,
				type: 'product',
				section_id: sectionId,
			});
			url = `/search?${params}`;
		} else {
			const limit = this.dataset.resultsLimit || '8';
			const params = new URLSearchParams({
				q: query,
				'resources[type]': 'product',
				'resources[limit]': limit,
				section_id: sectionId,
			});
			url = `/search/suggest?${params}`;
		}

		try {
			const response = await fetch(url, {
				signal: this.#fetchController.signal,
			});

			if (!response.ok) return;

			const html = await response.text();
			const resultsInner = parseSection(html, '[data-results-inner]');

			if (!resultsInner || !this.refs.results) return;

			this.refs.results.innerHTML = resultsInner.innerHTML;
			this.#applyCardSettings();
			this.#showResults();

			// Set up infinite scroll + image preloader if applicable
			if (this.#isInfinite) {
				this.#setupInfiniteScroll();
				this.#setupImagePreloader();
			}

			const cards = this.refs.results.querySelectorAll('product-card');
			if (this.refs.status) {
				this.refs.status.textContent =
					cards.length > 0
						? `${cards.length} results available`
						: 'No results found';
			}

			eventBus.emit(new SearchResultsUpdatedEvent({ data: { query, resultsCount: cards.length } }));
		} catch (error) {
			if (/** @type {Error} */ (error).name === 'AbortError') return;
			console.error('SearchOverlay: fetch failed', error);
		}
	}

	#setupInfiniteScroll() {
		this.#teardownInfiniteScroll();

		const grid = this.refs.results?.querySelector('[data-search-grid]');
		let loader = this.refs.results?.querySelector('[data-loader]');
		if (!grid) return;

		this.#nextUrl = grid.dataset.nextUrl || null;

		// Always ensure a loader element exists (may not be in initial Liquid if single page)
		if (!loader) {
			loader = document.createElement('div');
			loader.className = 'search-overlay-results__loader';
			loader.setAttribute('data-loader', '');
			loader.innerHTML = '<span class="search-overlay-results__spinner"></span>';
			grid.after(loader);
		}

		if (!this.#nextUrl) {
			loader.hidden = true;
			return;
		}

		loader.hidden = false;
		const triggerVh = parseInt(this.dataset.loadTriggerVh, 10) || 50;
		const rootMarginPx = Math.round(window.innerHeight * triggerVh / 100);

		this.#scrollObserver = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && !this.#isLoadingMore) {
					this.#loadMore();
				}
			},
			{ root: this.refs.panel, rootMargin: `0px 0px ${rootMarginPx}px 0px` },
		);
		this.#scrollObserver.observe(loader);
	}

	#teardownInfiniteScroll() {
		this.#scrollObserver?.disconnect();
		this.#scrollObserver = null;
		this.#nextUrl = null;
		this.#isLoadingMore = false;
	}

	async #loadMore() {
		if (!this.#nextUrl || this.#isLoadingMore) return;
		this.#isLoadingMore = true;

		try {
			const sectionId = this.dataset.sectionId || 'search-overlay-results';
			const url = this.#nextUrl.includes('section_id')
				? this.#nextUrl
				: `${this.#nextUrl}&section_id=${sectionId}`;

			const response = await fetch(url);
			if (!response.ok) return;

			const html = await response.text();
			const doc = new DOMParser().parseFromString(html, 'text/html');
			const newGrid = doc.querySelector('[data-search-grid]');
			const currentGrid = this.refs.results?.querySelector('[data-search-grid]');

			if (newGrid && currentGrid) {
				for (const child of [...newGrid.children]) {
					currentGrid.appendChild(child);
				}
				this.#applyCardSettings();

				// Observe new cards for image preloading
				if (this.#preloadObserver) {
					for (const child of currentGrid.querySelectorAll('product-card')) {
						if (!child.querySelector('img[loading="eager"]')) {
							this.#preloadObserver.observe(child);
						}
					}
				}

				// Update next URL from fetched page
				this.#nextUrl = newGrid.dataset.nextUrl || null;
			}

			// Hide loader if no more pages
			if (!this.#nextUrl) {
				const loader = this.refs.results?.querySelector('[data-loader]');
				if (loader) loader.hidden = true;
				this.#scrollObserver?.disconnect();
			}

			// Update count
			const cards = this.refs.results?.querySelectorAll('product-card');
			if (this.refs.status && cards) {
				this.refs.status.textContent = `${cards.length} results available`;
			}
		} catch (error) {
			console.error('SearchOverlay: loadMore failed', error);
		} finally {
			this.#isLoadingMore = false;
		}
	}
}

if (!customElements.get('search-overlay')) {
	customElements.define('search-overlay', SearchOverlay);
}
